import { Input } from "@repo/ui/input";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@repo/ui/ui/field";
import { useId, useState } from "react";

import type { DatasourceForm } from "./datasource-new.types";
import { parseSqlConnectionUrl } from "./parse-connection-url";

interface SqlFieldsProps {
  form: DatasourceForm;
  defaultPort: string;
  portLabel?: string;
  /** URL scheme used in the connection-string placeholder. */
  urlScheme?: string;
}

/** Shared credential fields for Postgres, MySQL, and Redshift. */
export const SqlFields = ({
  form,
  defaultPort,
  portLabel = "Port",
  urlScheme = "postgresql",
}: SqlFieldsProps) => {
  const urlFieldId = useId();
  const [connectionUrl, setConnectionUrl] = useState("");

  const handleConnectionUrlChange = (value: string) => {
    setConnectionUrl(value);
    const parsed = parseSqlConnectionUrl(value);
    if (!parsed) return;

    form.setFieldValue("host", parsed.host);
    form.setFieldValue("port", parsed.port || defaultPort);
    form.setFieldValue("database", parsed.database);
    form.setFieldValue("user", parsed.user);
    form.setFieldValue("password", parsed.password);
    form.setFieldValue("ssl", parsed.ssl);
  };

  return (
    <FieldGroup>
      <Field>
        <FieldLabel htmlFor={urlFieldId}>Connection URL</FieldLabel>
        <Input
          id={urlFieldId}
          value={connectionUrl}
          onChange={(e) => handleConnectionUrlChange(e.target.value)}
          placeholder={`${urlScheme}://user:password@host:${defaultPort}/database`}
          autoComplete="off"
        />
        <FieldDescription>
          Paste a connection string to auto-fill the fields below.
        </FieldDescription>
      </Field>

      <div className="grid grid-cols-3 gap-4">
        <form.AppField name="host">
          {(field) => (
            <field.TextField
              label="Host"
              placeholder="localhost"
              className="col-span-2"
            />
          )}
        </form.AppField>

        <form.AppField name="port">
          {(field) => (
            <field.TextField label={portLabel} placeholder={defaultPort} />
          )}
        </form.AppField>
      </div>

      <form.AppField name="database">
        {(field) => (
          <field.TextField label="Database" placeholder="my_database" />
        )}
      </form.AppField>

      <div className="grid grid-cols-2 gap-4">
        <form.AppField name="user">
          {(field) => <field.TextField label="User" placeholder="admin" />}
        </form.AppField>

        <form.AppField name="password">
          {(field) => (
            <field.PasswordField label="Password" placeholder="••••••••" />
          )}
        </form.AppField>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <form.AppField name="ssl">
          {(field) => (
            <field.SwitchField
              label="SSL"
              description="Enable SSL for encrypted connections."
            />
          )}
        </form.AppField>
      </div>
    </FieldGroup>
  );
};
